import { React, useState } from 'react';
import { Box, Button, IconButton, Popover, Skeleton, Stack, Typography } from '@mui/material';
import { ArrowDropUp as ArrowUpIcon, ArrowDropDown as ArrowDownIcon } from '@mui/icons-material';
import {
    CalendarToday as CalendarIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const CF_BLUE = '#4da6ff';

function ChangeChip({ change }) {
    if (change == null) return null;
    const up = change >= 0;
    const Icon = up ? ArrowUpIcon : ArrowDownIcon;
    return (
        <Stack direction='row' alignItems='center' sx={{ color: up ? 'success.main' : 'error.main', ml: 1 }}>
            <Icon sx={{ fontSize: 18, mr: '-2px' }} />
            <Typography variant='caption' fontWeight={600} sx={{ fontSize: 12 }}>
                {Math.abs(change).toFixed(1)}%
            </Typography>
        </Stack>
    );
}

function StatCard({ label, value, loading, sub, accent, sparkline, sparklineKey, change, decimals }) {
    const gradId = `spark-${sparklineKey ?? label.replace(/\s+/g, '-')}`;
    return (
        <Box
            sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Box sx={{ px: 2.5, pt: 2, pb: sparkline ? 1.5 : 2, flexShrink: 0 }}>
                <Typography
                    variant='caption'
                    sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.5,
                    }}>
                    {label}
                </Typography>
                {loading ? (
                    <Skeleton variant='text' width={80} height={44} />
                ) : (
                    <Stack direction='row' alignItems='center'>
                        <Typography
                            variant='h4'
                            fontWeight={700}
                            sx={{ color: accent ? 'primary.main' : 'text.primary', lineHeight: 1.1 }}>
                            {value ?? '—'}
                        </Typography>
                        {sub && (
                            <Typography variant='caption' sx={{ ml: 1, fontSize: 11, color: 'text.secondary' }}>
                                {sub}
                            </Typography>
                        )}
                        <ChangeChip change={change} />
                    </Stack>
                )}
            </Box>
            {sparkline && !loading && (
                <Box sx={{ height: 120 }}>
                    <ResponsiveContainer width='100%' height='100%'>
                        <AreaChart data={sparkline} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                            <defs>
                                <linearGradient id={gradId} x1='0' y1='0' x2='0' y2='1'>
                                    <stop offset='5%' stopColor={CF_BLUE} stopOpacity={0.2} />
                                    <stop offset='95%' stopColor={CF_BLUE} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid vertical={false} stroke='#9e9e9e' strokeOpacity={0.08} />
                            <XAxis dataKey='date' hide />
                            <YAxis
                                orientation='right'
                                allowDecimals={!!decimals}
                                width={decimals ? 48 : 36}
                                tick={{ fontSize: 10, fill: '#9e9e9e' }}
                                axisLine={false}
                                tickLine={false}
                                tickCount={4}
                                domain={[0, (dataMax) => Math.max(dataMax, decimals ? 0.01 : 1)]}
                            />
                            <Tooltip
                                wrapperStyle={{ transition: 'transform 200ms ease' }}
                                cursor={{ stroke: CF_BLUE, strokeWidth: 1, strokeDasharray: '4 2' }}
                                content={({ active, payload, label }) => {
                                    if (!active || !payload?.length) return null;
                                    return (
                                        <Box
                                            sx={{
                                                background: '#fff',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1.5,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                                px: 1.5,
                                                py: 1,
                                            }}>
                                            <Typography
                                                variant='caption'
                                                display='block'
                                                fontWeight={700}
                                                mb={0.5}
                                                sx={{ color: '#111' }}>
                                                {label}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: CF_BLUE,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography variant='caption' sx={{ color: '#333' }}>
                                                    {payload[0].name}
                                                </Typography>
                                                <Typography
                                                    variant='caption'
                                                    fontWeight={700}
                                                    sx={{ color: '#111', ml: 0.5 }}>
                                                    {payload[0].value?.toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                }}
                            />
                            <Area
                                type='monotone'
                                dataKey={sparklineKey ?? 'value'}
                                stroke={CF_BLUE}
                                strokeWidth={1.5}
                                fill={`url(#${gradId})`}
                                dot={false}
                                isAnimationActive={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Box>
    );
}

function ChartTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <Box
            sx={{
                background: 'rgba(30,30,30,0.92)',
                border: '1px solid #444',
                borderRadius: 1,
                px: 1,
                py: 0.5,
            }}>
            <Typography variant='caption' color='text.secondary' display='block'>
                {label}
            </Typography>
            {payload.map((p) => (
                <Typography key={p.dataKey} variant='caption' display='block' sx={{ color: p.color }}>
                    {p.name}: {p.value}
                </Typography>
            ))}
        </Box>
    );
}

const PRESETS = [
    { label: 'Last 1 hour', hours: 1 },
    { label: 'Last 6 hours', hours: 6 },
    { label: 'Last 12 hours', hours: 12 },
    { label: 'Last 24 hours', hours: 24 },
    { label: 'Last 7 days', hours: 168 },
    { label: 'Last 30 days', hours: 720 },
];

function fmtDate(d) {
    return d.toISOString().slice(0, 10);
}

function calGrid(year, month) {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevDays = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = firstDay - 1; i >= 0; i--) cells.push({ d: new Date(year, month - 1, prevDays - i), cur: false });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ d: new Date(year, month, d), cur: true });
    while (cells.length % 7 !== 0) {
        const last = cells[cells.length - 1].d;
        cells.push({ d: new Date(last.getTime() + 86400000), cur: false });
    }
    return cells;
}

function DateRangePicker({ hours, onChange }) {
    const [anchor, setAnchor] = useState(null);
    const [view, setView] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [sel, setSel] = useState(null); // { start: Date, end: Date|null }
    const [hover, setHover] = useState(null);

    const open = Boolean(anchor);
    const label = PRESETS.find((p) => p.hours === hours)?.label ?? `Last ${hours}h`;

    function handleDay(d) {
        if (!sel || sel.end) {
            setSel({ start: d, end: null });
        } else {
            const [a, b] = d < sel.start ? [d, sel.start] : [sel.start, d];
            setSel({ start: a, end: b });
        }
    }

    function handleApply() {
        if (!sel?.start || !sel?.end) return;
        const diffHours = Math.ceil((new Date() - sel.start) / 3600000);
        onChange(Math.max(1, diffHours));
        setAnchor(null);
    }

    function handlePreset(p) {
        onChange(p.hours);
        setSel(null);
        setAnchor(null);
    }

    const cells = calGrid(view.getFullYear(), view.getMonth());
    const monthLabel = view.toLocaleString('en-US', { month: 'long', year: 'numeric' });

    return (
        <>
            <Button
                size='small'
                variant='outlined'
                startIcon={<CalendarIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', borderRadius: 2, fontSize: 13, px: 1.5 }}>
                {label}
            </Button>
            <Popover
                open={open}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                        border: '1px solid',
                        borderColor: 'divider',
                    },
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: 480 }}>
                    <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider' }}>
                        {/* Calendar */}
                        <Box sx={{ flex: 1, p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.5,
                                }}>
                                <Typography fontWeight={700} fontSize={14}>
                                    {monthLabel}
                                </Typography>
                                <Box>
                                    <IconButton
                                        size='small'
                                        onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1))}>
                                        <ChevronLeftIcon fontSize='small' />
                                    </IconButton>
                                    <IconButton
                                        size='small'
                                        onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1))}>
                                        <ChevronRightIcon fontSize='small' />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
                                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((h) => (
                                    <Typography
                                        key={h}
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{ textAlign: 'center', fontSize: 11 }}>
                                        {h}
                                    </Typography>
                                ))}
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                                {cells.map(({ d, cur }, i) => {
                                    const s = sel?.start;
                                    const e = sel?.end ?? (s && hover && hover >= s ? hover : null);
                                    const t = d.getTime();
                                    const isStart = s && t === s.getTime();
                                    const isEnd = e && t === e.getTime();
                                    const isEdge = isStart || isEnd;
                                    const inRange = s && e && t > s.getTime() && t < e.getTime();
                                    return (
                                        <Box
                                            key={i}
                                            onClick={() => handleDay(d)}
                                            onMouseEnter={() => setHover(d)}
                                            onMouseLeave={() => setHover(null)}
                                            sx={{
                                                textAlign: 'center',
                                                py: 0.5,
                                                cursor: 'pointer',
                                                borderRadius: isEdge ? 1 : 0,
                                                bgcolor: isEdge
                                                    ? 'text.primary'
                                                    : inRange
                                                      ? 'action.selected'
                                                      : 'transparent',
                                                '&:hover': {
                                                    bgcolor: isEdge ? 'text.primary' : 'action.hover',
                                                },
                                            }}>
                                            <Typography
                                                variant='body2'
                                                fontSize={13}
                                                sx={{
                                                    color: isEdge
                                                        ? 'background.paper'
                                                        : cur
                                                          ? 'text.primary'
                                                          : 'text.disabled',
                                                }}>
                                                {d.getDate()}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                        {/* Presets */}
                        <Box sx={{ width: 156, py: 1 }}>
                            {PRESETS.map((p) => (
                                <Box
                                    key={p.hours}
                                    onClick={() => handlePreset(p)}
                                    sx={{
                                        px: 2,
                                        py: 0.75,
                                        cursor: 'pointer',
                                        borderRadius: 1,
                                        mx: 0.5,
                                        bgcolor: hours === p.hours && !sel?.end ? 'action.selected' : 'transparent',
                                        '&:hover': { bgcolor: 'action.hover' },
                                    }}>
                                    <Typography variant='body2' fontSize={13}>
                                        {p.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    {/* Start / End / Apply */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, px: 2, py: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='caption' color='text.secondary' display='block' mb={0.5}>
                                Start
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 0.75,
                                }}>
                                <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography
                                    variant='body2'
                                    fontSize={13}
                                    color={sel?.start ? 'text.primary' : 'text.disabled'}>
                                    {sel?.start ? fmtDate(sel.start) : '—'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='caption' color='text.secondary' display='block' mb={0.5}>
                                End
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 0.75,
                                }}>
                                <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography
                                    variant='body2'
                                    fontSize={13}
                                    color={sel?.end ? 'text.primary' : 'text.disabled'}>
                                    {sel?.end ? fmtDate(sel.end) : '—'}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant='contained'
                            size='small'
                            disabled={!sel?.start || !sel?.end}
                            onClick={handleApply}
                            sx={{ borderRadius: 2, px: 2.5, mb: 0 }}>
                            Apply
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}

export { StatCard, ChartTooltip, DateRangePicker };
